import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { marked } from 'marked'
import { api } from '../api/client'
import { Icon } from '../components/Icon'
import { MarkdownContent } from '../plugins/renderContent'
import {
  getCurrentDocument, subscribeDocument, docKey, type DocRef,
} from '../lib/documentViewer'

// ─── Document panel ────────────────────────────────────────────────────────────
//
// The split-panel reader for a Markdown document opened from a chat attachment card.
// It tracks the documentViewer store (last-opened doc wins) and resolves the content:
//   • inline → the markdown travels with the ref (the agent printed it in chat)
//   • file   → fetched from /api/chats/:id/file (sandboxed to the chat cwd)
//   • url    → fetched from /api/chats/:id/fetch-md (server-side, CORS-free)
// Rendering goes straight through marked (NOT renderContent) so the doc body doesn't
// recursively sprout its own attachment cards.

marked.setOptions({ gfm: true, breaks: true })

type Load =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; html: string }
  | { phase: 'error'; message: string }

export function DocumentPanel({ chatId, onClose }: { chatId: string; onClose: () => void }) {
  // Subscribe to the open-document store. The card sets it; we re-render on change.
  const doc = useSyncExternalStore(subscribeDocument, getCurrentDocument)
  const [load, setLoad] = useState<Load>({ phase: 'idle' })

  const resolve = useCallback(async (ref: DocRef): Promise<string> => {
    if (ref.kind === 'inline') return ref.content ?? ''
    const cid = ref.chatId ?? chatId
    if (ref.kind === 'url' && ref.src)  return (await api.chats.fetchMarkdown(cid, ref.src)).content
    if (ref.kind === 'file' && ref.src) return (await api.chats.readDoc(cid, ref.src)).content
    return ''
  }, [chatId])

  const reload = useCallback((ref: DocRef) => {
    setLoad({ phase: 'loading' })
    let alive = true
    resolve(ref)
      .then(content => { if (alive) setLoad({ phase: 'ready', html: marked.parse(content) as string }) })
      .catch(err => { if (alive) setLoad({ phase: 'error', message: String(err?.message || err) }) })
    return () => { alive = false }
  }, [resolve])

  // Re-resolve whenever the open doc changes (identity, not object reference).
  useEffect(() => {
    if (!doc) { setLoad({ phase: 'idle' }); return }
    return reload(doc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc && docKey(doc), reload])

  return (
    <div className="doc-panel">
      <div className="doc-panel-head">
        <span className="doc-panel-icon"><Icon name="file-text" size={16} /></span>
        <span className="doc-panel-title" title={doc?.src ?? doc?.name}>{doc?.name ?? 'Document'}</span>
        {doc && doc.kind !== 'inline' && (
          <button
            className="btn-icon doc-panel-refresh"
            onClick={() => reload(doc)}
            title="Reload from disk"
            aria-label="Reload document"
          >
            <Icon name="refresh" size={15} />
          </button>
        )}
        <button className="btn-icon doc-panel-close" onClick={onClose} title="Close" aria-label="Close document">
          <Icon name="x" size={16} />
        </button>
      </div>

      <div className="doc-panel-body">
        {load.phase === 'idle' && <div className="doc-panel-empty">เลือกไฟล์ .md จากการ์ดในแชทเพื่ออ่าน</div>}
        {load.phase === 'loading' && <div className="doc-panel-loading"><span className="spin-icon">◌</span> กำลังโหลด...</div>}
        {load.phase === 'error' && <div className="doc-panel-error">เปิดไฟล์ไม่ได้: {load.message}</div>}
        {load.phase === 'ready' && (
          <MarkdownContent html={load.html} className="doc-panel-md" />
        )}
      </div>
    </div>
  )
}
